import { Button } from '@creditornot/cb-button';
import { InfoCircleFilled } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useDirection } from '@creditornot/cb-provider';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useMutation } from '@tanstack/react-query';
import React, { MouseEvent } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { sendCredits } from 'apis/restaurant-api/merchant-admin/credits/api';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { typographyCss } from 'common/typography';

import Offer from './icons/Offer';
import Voucher from './icons/Voucher';
import { OfferMonetaryAmount, getAvailableOfferForCurrency } from './utils';
import { telemetryRatingsAndReviewsSendOfferClick, telemetryRatingsAndReviewsSendOfferHover } from '../../../telemetry';

export type SendOfferProps = {
  venueId: string;
  currency: Currency;
  purchaseId: string;
  onSendOffer: () => void;
};

const OfferButton = styled.div`
  ${typographyCss.Body3}
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colors.wolt100};
  cursor: pointer;

  svg {
    fill: ${colors.wolt100};
    flex-shrink: 0;
    height: 20px;
    width: 20px;
  }
`;

const PopoverRoot = styled.div``;

const Row = styled.div`
  padding: 12px 16px;
`;

const Header = styled(Row)`
  ${typographyCss.Body3}
  font-weight: 600;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;
const Content = styled(Row)`
  ${typographyCss.Body3}
`;
const ContentItem = styled.div`
  display: flex;
  gap: 8px;

  svg {
    fill: ${cbSemanticCssVars.textPrimary};
    flex-shrink: 0;
    height: 20px;
    width: 20px;
  }
`;
const Footer = styled(Row)`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  display: flex;
  gap: 24px;
  justify-content: space-between;
  align-items: center;
`;

const ValidityText = styled.div`
  ${typographyCss.Small}
  color: ${cbSemanticCssVars.textSecondary};
`;

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  fill: ${cssThemedValue({ light: colors.pepper24, dark: colors.salt28 })};
  margin-inline-start: 4px;
  margin-block-end: -2px; /* visually align icon with text */
`;
const Tooltip = styled.div`
  ${typographyCss.Body3};
  padding: 4px;
  line-height: 20px;
  max-width: 398px;
`;

const SendOffer = ({ venueId, currency, purchaseId, onSendOffer }: SendOfferProps) => {
  const cbDirection = useDirection();

  const sendOfferMutation = useMutation(() => sendCredits(venueId, purchaseId), {
    onSuccess: () => {
      onSendOffer();
    },
    onError: error => {
      toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
    },
  });

  const handleSendOffer = (event: MouseEvent) => {
    event.stopPropagation();
    if (sendOfferMutation.isLoading) {
      return;
    }
    sendOfferMutation.mutate();
    telemetryRatingsAndReviewsSendOfferClick();
  };

  const handleSendOfferOnHover = () => telemetryRatingsAndReviewsSendOfferHover();

  const availableOffer = getAvailableOfferForCurrency(currency);

  if (!availableOffer) {
    return null;
  }

  return (
    <LegacyPopover
      placement={cbDirection === 'rtl' ? 'bottom-end' : 'bottom-start'}
      fallbackPlacements={
        cbDirection === 'rtl'
          ? ['bottom', 'bottom-start', 'top-end', 'top', 'top-start']
          : ['bottom', 'bottom-end', 'top-start', 'top', 'top-end']
      }
      disableContentPadding
      contentZIndex={9999}
      renderArrow={false}
      showOnHover
      showOnClick
      content={
        <PopoverRoot dir={cbDirection}>
          <Header>
            <FormattedMessage id="insights.ratings-and-reviews.offers.send-offer-title" />
            <LegacyPopover
              contentZIndex={99999}
              content={
                <Tooltip>
                  <FormattedMessage id={'insights.ratings-and-reviews.offers.send-offer-tooltip'} />
                </Tooltip>
              }
            >
              <InfoIcon />
            </LegacyPopover>
          </Header>
          <Content>
            <ContentItem>
              <Voucher />
              <FormattedMessage
                id="insights.ratings-and-reviews.offers.offer-description"
                values={{
                  amountAndUnit: <OfferMonetaryAmount {...availableOffer} />,
                }}
                tagName="span"
              />
            </ContentItem>
          </Content>
          <Footer>
            <ValidityText>
              <FormattedMessage
                id="insights.ratings-and-reviews.offers.offer-validity-days"
                values={{ days: availableOffer.validityInDays }}
              />
            </ValidityText>
            <Button variant="blue" size="tiny" loading={sendOfferMutation.isLoading} onClick={handleSendOffer}>
              <FormattedMessage id="insights.ratings-and-reviews.offers.send-button" />
            </Button>
          </Footer>
        </PopoverRoot>
      }
    >
      <OfferButton onMouseOver={handleSendOfferOnHover}>
        <Offer />
        <FormattedMessage id="insights.ratings-and-reviews.offers.send-offer" />
      </OfferButton>
    </LegacyPopover>
  );
};

export default SendOffer;
